<script setup lang="ts">
import type { EstateDetailInterface } from '~/types/api/EstateDetail'
import type { EstateListing } from '~/types/api/EstateListing'

const props = defineProps<{
  estateData: EstateListing | EstateDetailInterface
}>()

const parameters = useParameters(props.estateData).filter((parameter) => {
  // CBRE-119 Filter out units count
  return !(
    parameter?.label &&
    (parameter.label === 'Počet prevádzok' || parameter.label === 'Units count')
  )
})
</script>

<template>
  <div class="c-estate-parameters">
    <h2 class="c-estate-parameters__title h4">
      {{ $t('pages.estateDetail.parametersTitle', { _0: estateData.name }) }}
    </h2>

    <div class="c-estate-parameters__content">
      <template v-for="(parameter, index) in parameters" :key="parameter.id">
        <div
          v-if="index !== 0 && index % 3 === 0"
          class="c-estate-parameters__item c-estate-parameters__item--divider"
        ></div>

        <div class="c-estate-parameters__item">
          <img class="sc-image" :src="parameter.image" :alt="parameter.label" />

          <div class="sc-content">
            <span v-html="parameter.label" />
            <strong v-html="parameter.value" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-estate-parameters {
  &__title {
    margin-bottom: 32px;

    @include breakpoint-down('xs') {
      margin-bottom: 16px;
    }
  }

  &__content {
    padding: 40px 48px;
    background-color: var(--color-solid-grey);
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;

    @include breakpoint-down('md') {
      padding: 32px;
      gap: 24px;
    }

    @include breakpoint-down('sm') {
      grid-template-columns: repeat(2, 1fr);
    }

    @include breakpoint-down('xs') {
      gap: 20px;
      grid-template-columns: 1fr;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;

    &--divider {
      grid-column: 1 / -1;
      height: 1px;
      background-color: var(--color-border);

      @include breakpoint-down('sm') {
        display: none;
      }
    }

    .sc-image {
      width: 28px;
      height: 28px;
      object-fit: contain;
    }

    .sc-content {
      display: flex;
      flex-direction: column;
      line-height: 1.2;

      strong {
        color: var(--color-primary);
      }
    }
  }
}
</style>
